import type { ThreadType } from '@form-factory/schema';
import {
  Currency,
  ThreadCategory,
  ThreadMode,
} from '@form-factory/schema/enums';
import { formatUtils, DateFormat } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import * as Tabs from '@radix-ui/react-tabs';
import { Badge, Button, cn } from '@utima/ui';
import { MessageCircle, X } from 'lucide-react';

import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { ThreadCosts } from '@/features/threads';
import { useEnumTranslations, useUser } from '@/hooks';

export interface ThreadDetailsProps {
  thread: ThreadType;
  className?: string;
  onClose: () => void;
}

export function ThreadDetails({
  thread,
  className,
  onClose,
}: ThreadDetailsProps) {
  const { t } = useLingui();
  const { threadCategory } = useEnumTranslations();
  const { organization } = useUser();

  const organizationCurrency = organization?.currency ?? Currency.USD;

  return (
    <div
      className={cn(
        'w-[500px] sm:block hidden border-l border-gray-200 bg-white p-4 h-full overflow-auto',
        className,
      )}
    >
      <div className='mb-2 flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>
          <Trans>Thread Information</Trans>
        </h3>
        <Button
          data-cta='admin-inbox-thread-details-close-button'
          variant='ghost'
          size='icon-sm'
          className='size-8 p-0'
          onClick={onClose}
        >
          <X className='size-4' />
        </Button>
      </div>

      <Tabs.Root
        className='flex min-h-0 flex-1 flex-col'
        defaultValue='metadata'
      >
        <Tabs.List className='mb-4 border-b border-border'>
          <Tabs.Trigger
            value='metadata'
            className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
          >
            <Trans>Metadata</Trans>
          </Tabs.Trigger>

          <Tabs.Trigger
            value='costs'
            className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
          >
            <Trans>Costs</Trans>
          </Tabs.Trigger>
        </Tabs.List>

        <div className='min-h-0 flex-1'>
          <Tabs.Content value='metadata' className='h-full'>
            <div className='space-y-4'>
              <DisplayValue label={t`Thread ID`} className='text-sm'>
                {thread.id}
              </DisplayValue>

              <DisplayValue label={t`Category`} className='text-sm'>
                <Badge
                  className='inline-flex items-center gap-2'
                  variant='primary'
                >
                  <MessageCircle className='size-3.5' />
                  {threadCategory[thread.category]}
                </Badge>
              </DisplayValue>

              <DisplayValue label={t`Created`} className='text-sm'>
                {formatUtils.date(
                  thread.createdAt,
                  DateFormat.DateTimeNumericSeconds,
                )}
              </DisplayValue>

              <DisplayValue label={t`Updated`} className='text-sm'>
                {formatUtils.date(
                  thread.updatedAt,
                  DateFormat.DateTimeNumericSeconds,
                )}
              </DisplayValue>

              <DisplayValue label={t`Last user message`} className='text-sm'>
                {thread.lastUserMessageAt
                  ? formatUtils.date(
                      thread.lastUserMessageAt,
                      DateFormat.DateTimeNumericSeconds,
                    )
                  : '-'}
              </DisplayValue>

              <DisplayValue label={t`Last active`} className='text-sm'>
                {thread.lastActiveAt
                  ? formatUtils.date(
                      thread.lastActiveAt,
                      DateFormat.DateTimeNumericSeconds,
                    )
                  : '-'}
              </DisplayValue>

              {thread.category === ThreadCategory.Chat && (
                <DisplayValue label={t`Conversation mode`} className='text-sm'>
                  <Badge
                    size='sm'
                    variant={
                      thread.mode === ThreadMode.AI ? 'secondary' : 'primary'
                    }
                  >
                    {thread.mode === ThreadMode.AI ? 'AI' : 'Human'}
                  </Badge>
                </DisplayValue>
              )}

              <DisplayValue label={t`Message Count`} className='text-sm'>
                {thread.messages?.length || 0}
              </DisplayValue>
            </div>
          </Tabs.Content>

          <Tabs.Content value='costs' className='h-full'>
            <ThreadCosts threadId={thread.id} currency={organizationCurrency} />
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </div>
  );
}
